import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogContent, DialogTitle, TextField, InputAdornment, Box, Typography, IconButton, Stack } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { FieldItem } from "../FieldItem";
import { FieldListData, getFieldCopyConfig, getFieldCategoryLabels } from "../../configs";
import { AppState } from "../../../../../../../shell/store/types";
import { User } from "../../../../../../../shell/services/types";
interface Props {
  onFieldClick: (fieldType: string, fieldName: string) => void;
  onModalClose: () => void;
}
export const FieldSelection = ({
  onFieldClick,
  onModalClose
}: Props) => {
  const {
    t
  } = useTranslation();
  const FIELD_COPY_CONFIG = getFieldCopyConfig(t);
  const FIELD_CATEGORY_LABELS = getFieldCategoryLabels(t);
  const user: User = useSelector((state: AppState) => state.user);
  const [fieldTypes, setFieldTypes] = useState(FIELD_COPY_CONFIG);
  const handleFilterFields = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userInput = e.target.value.toLowerCase();
    let filteredFields: {
      [key: string]: FieldListData[];
    } = {};
    Object.keys(FIELD_COPY_CONFIG).forEach(category => {
      const matchedFields = FIELD_COPY_CONFIG[category].filter(field => {
        const name = field.name.toLowerCase();
        const mediaFileKeywords = ["file", "image", "picture", "pdf", "document", "spreadsheet", "zip", "audio", "powerpoint", "video"];
        if (name.includes(userInput)) {
          return field;
        }
        if (name === "media") {
          const inputRegex = new RegExp(userInput, "gi");
          const matchedMediaKeywords = mediaFileKeywords.some(keyword => inputRegex.test(keyword));
          if (matchedMediaKeywords) {
            return field;
          }
        }
      });
      if (matchedFields.length) {
        filteredFields[category] = matchedFields;
      }
    });
    setFieldTypes(filteredFields);
  };
  return <>
      <DialogTitle component="div" data-sentry-element="DialogTitle" data-sentry-source-file="FieldSelection.tsx">
        <Stack flexDirection="row" justifyContent="space-between" alignItems="flex-start" data-sentry-element="Stack" data-sentry-source-file="FieldSelection.tsx">
          <Stack gap={2} data-sentry-element="Stack" data-sentry-source-file="FieldSelection.tsx">
            <Typography variant="h5" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="FieldSelection.tsx">
              {t("schema.selectFieldTypeTitle")}
            </Typography>
            <Box width="349px" data-sentry-element="Box" data-sentry-source-file="FieldSelection.tsx">
              <TextField data-cy="FieldSelectionFilter" fullWidth InputProps={{
              startAdornment: <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
            }} onChange={handleFilterFields} placeholder={t("schema.searchFieldTypesPlaceholder")} autoFocus size="small" data-sentry-element="TextField" data-sentry-source-file="FieldSelection.tsx" />
            </Box>
          </Stack>
          <IconButton size="small" onClick={onModalClose} data-cy="AddFieldCloseBtn" data-sentry-element="IconButton" data-sentry-source-file="FieldSelection.tsx">
            <CloseIcon fontSize="small" data-sentry-element="CloseIcon" data-sentry-source-file="FieldSelection.tsx" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent data-cy="FieldSelection" dividers sx={{
      pt: 2.5,
      backgroundColor: "grey.50",
      "&.MuiDialogContent-dividers": {
        borderColor: "border"
      },
      "& div.field-type-group:not(:last-of-type)": {
        mb: 1.5
      }
    }} data-sentry-element="DialogContent" data-sentry-source-file="FieldSelection.tsx">
        {!Object.keys(fieldTypes).length && <Typography data-cy="FieldSelectionEmpty">
            {t("schema.noMatchesFound")}
          </Typography>}
        {Object.keys(fieldTypes).map(fieldKey => <Box className="field-type-group" key={fieldKey}>
            <Typography component="p" variant="overline" mb={1.5} color="text.secondary">
              {FIELD_CATEGORY_LABELS?.[fieldKey] || fieldKey}
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap={1.5} columnGap={2}>
              {fieldTypes[fieldKey].map((field: FieldListData, index) => {
            return <FieldItem key={index} fieldName={field.name} shortDescription={field.shortDescription} fieldType={field.type} description={field.description} commonUses={field.commonUses} proTip={field.proTip} onFieldClick={() => onFieldClick(field.type, field.name)} />;
          })}
            </Box>
          </Box>)}
      </DialogContent>
    </>;
};